import { OnboardingMessageKey } from '../../translations/messages';
import { en } from '../../translations/en';

export interface Journey {
  id: string;
  title: string;
  duration: string;
  description: string;
  totalSteps: number;
  phases: string[];
}

export interface Role {
  id: string;
  title: string;
  description: string;
  duration: string;
  journeys: Journey[];
  points: string[];
  badgeExtra: string;
}

/**
 * Build the ROLES array using the translation function `t`.
 *
 * This keeps the data structure and IDs stable while allowing every
 * user-visible string to be translated.  When Backstage's i18n ships,
 * the `t` parameter will come from `useTranslationRef` instead.
 */
export const getRoles = (
  t: (key: OnboardingMessageKey) => string,
): Role[] => [
  {
    id: 'developer',
    title: t('roleDeveloperTitle'),
    description: t('roleDeveloperDescription'),
    duration: t('roleDeveloperDuration'),
    journeys: [
      {
        id: 'navigating',
        title: t('journeyNavigatingTitle'),
        duration: t('journeyNavigatingDurationDev'),
        description: t('journeyNavigatingDesc'),
        totalSteps: 16,
        phases: [
          t('phaseWelcome'),
          t('phaseNavigating'),
          t('phaseFinding'),
          t('phaseExternal'),
          t('phasePermissions'),
        ],
      },
      {
        id: 'deploy',
        title: t('journeyDeployTitle'),
        duration: t('journeyDeployDuration'),
        description: t('journeyDeployDesc'),
        totalSteps: 12,
        phases: [
          t('phasePipeline'),
          t('phaseBuild'),
          t('phaseStaging'),
          t('phaseProduction'),
          t('phaseMonitoringRollback'),
        ],
      },
      {
        id: 'troubleshoot',
        title: t('journeyTroubleshootTitle'),
        duration: t('journeyTroubleshootDuration'),
        description: t('journeyTroubleshootDesc'),
        totalSteps: 10,
        phases: [
          t('phaseIdentifying'),
          t('phaseReadingLogs'),
          t('phasePipelineErrors'),
          t('phaseDebugging'),
          t('phaseEscalation'),
        ],
      },
    ],
    points: [
      t('roleDeveloperPoint1'),
      t('roleDeveloperPoint2'),
      t('roleDeveloperPoint3'),
    ],
    badgeExtra: t('roleDeveloperBadge'),
  },
  {
    id: 'techlead',
    title: t('roleTechLeadTitle'),
    description: t('roleTechLeadDescription'),
    duration: t('roleTechLeadDuration'),
    journeys: [
      {
        id: 'navigating',
        title: t('journeyNavigatingTitle'),
        duration: t('journeyNavigatingDurationTL'),
        description: t('journeyNavigatingDesc'),
        totalSteps: 16,
        phases: [
          t('phaseWelcome'),
          t('phaseNavigating'),
          t('phaseFinding'),
          t('phaseExternal'),
          t('phasePermissions'),
        ],
      },
      {
        id: 'deploy',
        title: t('journeyDeployTitle'),
        duration: t('journeyDeployDuration'),
        description: t('journeyDeployDesc'),
        totalSteps: 12,
        phases: [
          t('phasePipeline'),
          t('phaseBuild'),
          t('phaseStaging'),
          t('phaseProduction'),
          t('phaseMonitoringRollback'),
        ],
      },
      {
        id: 'troubleshoot',
        title: t('journeyTroubleshootTitle'),
        duration: t('journeyTroubleshootDuration'),
        description: t('journeyTroubleshootDesc'),
        totalSteps: 10,
        phases: [
          t('phaseIdentifying'),
          t('phaseReadingLogs'),
          t('phasePipelineErrors'),
          t('phaseDebugging'),
          t('phaseEscalation'),
        ],
      },
      {
        id: 'templates',
        title: t('journeyTemplatesTitle'),
        duration: t('journeyTemplatesDuration'),
        description: t('journeyTemplatesDesc'),
        totalSteps: 8,
        phases: [
          t('phaseTemplateGallery'),
          t('phaseCreatingComponent'),
          t('phaseConfiguring'),
          t('phaseRepoSetup'),
          t('phaseFirstDeploy'),
        ],
      },
      {
        id: 'azure',
        title: t('journeyAzureTitle'),
        duration: t('journeyAzureDuration'),
        description: t('journeyAzureDesc'),
        totalSteps: 14,
        phases: [
          t('phaseAzureOverview'),
          t('phaseKeyVault'),
          t('phaseStorage'),
          t('phaseEventHub'),
          t('phaseResourceMonitoring'),
        ],
      },
      {
        id: 'api',
        title: t('journeyApiTitle'),
        duration: t('journeyApiDuration'),
        description: t('journeyApiDesc'),
        totalSteps: 10,
        phases: [
          t('phaseApiStandards'),
          t('phaseScorecard'),
          t('phaseRegisterApis'),
          t('phaseCompliance'),
          t('phaseImprovingScores'),
        ],
      },
      {
        id: 'team',
        title: t('journeyTeamTitle'),
        duration: t('journeyTeamDuration'),
        description: t('journeyTeamDesc'),
        totalSteps: 8,
        phases: [
          t('phaseTeamStructure'),
          t('phaseRoleAccess'),
          t('phasePermissionPolicies'),
          t('phaseOnboardingMembers'),
        ],
      },
      {
        id: 'cost',
        title: t('journeyCostTitle'),
        duration: t('journeyCostDuration'),
        description: t('journeyCostDesc'),
        totalSteps: 8,
        phases: [
          t('phaseCostDashboard'),
          t('phaseUsageAnalysis'),
          t('phaseOptimization'),
          t('phaseBudgetAlerts'),
        ],
      },
    ],
    points: [
      t('roleTechLeadPoint1'),
      t('roleTechLeadPoint2'),
      t('roleTechLeadPoint3'),
      t('roleTechLeadPoint4'),
    ],
    badgeExtra: t('roleTechLeadBadge'),
  },
];

/**
 * ROLES constant for backward compatibility (English defaults).
 * Prefer `getRoles(t)` inside components that have translation context.
 */
export const ROLES: Role[] = getRoles(key => en[key] ?? key);
